import { getSbahnPlugin } from '@sie-private/telemetry-js';
import {
  isUsingEvidon, isUsingOneTrust, EvidonClient, OneTrustClient,
} from '@sie/web-common-react-components';
import { unifiedDefaultSchema } from '@rnps-ppr/telemetry-client-events';
import parser from 'ua-parser-js';
import getCookie from '../getCookie';
import getUserSignInStatusOracle from './getUserSignInStatusOracle';
import getTelemetryUserData from './getTelemetryUserData';
import { buildUserTelemetryFields, resolveSignedInStatus } from './getUserTelemetryPayload';
import getLineFromHost from '../getLineFromHost';
import TOOLBAR from '../../constants/toolbar';

const config = {
  appName: 'jetstream',
  appVersion: '0.0.0',
  charset: 'utf-8',
  eventSchemas: [unifiedDefaultSchema],
  logLevel: 'off',
  overrides: {
    criteria: {
      line: 'e1-np|q1-np|sp-int|qa2|euqa',
    },
    overrideConfig: {
      logLevel: 'debug',
    },
  },
  platform: 'web',
  plugins: [getSbahnPlugin()],
  signedIn: '',
};

const getParsedUserAgent = () => parser(window.navigator.userAgent);

const resolveBrowserName = (browserName = '') => {
  const normalizedBrowserName = browserName.toLowerCase();

  if (normalizedBrowserName.includes('edge')) return 'EDGE';
  if (normalizedBrowserName.includes('firefox')) return 'FIREFOX';
  if (normalizedBrowserName.includes('safari')) return 'SAFARI';
  if (normalizedBrowserName.includes('chrome') || normalizedBrowserName.includes('chromium')) {
    return 'CHROME';
  }

  return 'NOT_SUPPORTED';
};

const getUserAgentTelemetryFields = () => {
  const { device, os, browser } = getParsedUserAgent();
  const deviceType = device?.type;

  return {
    webDeviceType: deviceType === 'mobile' || deviceType === 'tablet' ? 'mobile' : 'desktop',
    webDeviceModel: device?.model || '',
    webOsName: os?.name || '',
    webOsVersion: os?.version || '',
    browserName: resolveBrowserName(browser?.name),
    browserVersion: browser?.version || '',
  };
};

const getTelemetryJsPlatformPrivacyLevel = (async () => {
  let privacyPreference = 'minimal';

  if (!isUsingEvidon() && !isUsingOneTrust()) {
    const euCookiePreference = getCookie('eucookiepreference');
    if (!euCookiePreference || euCookiePreference === 'accept') {
      privacyPreference = 'all';
    }
  } else {
    const GdprClient = isUsingEvidon() ? EvidonClient : OneTrustClient;
    await new GdprClient().getPrivacyPreference().then((privacyPolicy) => {
      privacyPreference = privacyPolicy;
    });
    return privacyPreference;
  }
  return privacyPreference;
});

const getWebDeviceEffectiveNetworkType = () => {
  const connection = window.navigator.connection
    || window.navigator.mozConnection
    || window.navigator.webkitConnection;

  return connection?.effectiveType || '';
};

const getDimensions = () => {
  if (typeof window === 'undefined' || !window.screen) {
    return {};
  }

  return {
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    browserWidth: window.innerWidth,
    browserHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
  };
};

const applyUserTelemetryFields = (telemetryJsConfig, userData, isSignedIn) => {
  Object.assign(telemetryJsConfig, buildUserTelemetryFields(userData, isSignedIn));
};

export default async function ({ webToolbarType } = {}) {
  // match location with overrides
  const { navigator, location } = window;
  const { userAgent } = navigator;
  const { href: pageURL, hostname = '' } = location || {};
  const referrerURL = document.referrer;
  const linePattern = new RegExp(config.overrides.criteria.line);
  const isDevEnv = hostname.match(linePattern)?.length > 0;
  const appVersion = process.env.JET_VERSION || config.appVersion;
  const {
    screenWidth,
    screenHeight,
    browserWidth,
    browserHeight,
    devicePixelRatio,
  } = getDimensions();
  const telemetryJsConfig = isDevEnv ? Object.assign({}, config, config.overrides.overrideConfig)
    : config;
  Object.assign(telemetryJsConfig, {
    appVersion,
    platformPrivacyWs1: await getTelemetryJsPlatformPrivacyLevel(),
    npEnv: getLineFromHost(hostname, { default: 'np' }),
    userAgent,
    pageURL,
    referrerURL,
    screenWidth,
    screenHeight,
    browserWidth,
    browserHeight,
    devicePixelRatio,
    ...getUserAgentTelemetryFields(),
    webDeviceEffectiveNetworkType: getWebDeviceEffectiveNetworkType(),
    hostAppName: hostname,
  });

  const finalizeConfig = (userData, toolbarSignedIn) => {
    const isSignedIn = resolveSignedInStatus(
      typeof userData.isLoggedIn === 'boolean'
        ? userData
        : { ...userData, isLoggedIn: toolbarSignedIn },
    );
    applyUserTelemetryFields(telemetryJsConfig, userData, isSignedIn);
    return telemetryJsConfig;
  };

  const {
    name: oracleName,
  } = TOOLBAR.ORACLE;

  if (webToolbarType === oracleName) {
    return new Promise((resolve) => {
      getUserSignInStatusOracle()
        .then((isSignedIn) => {
          resolve(finalizeConfig(getTelemetryUserData(), isSignedIn));
        });
    });
  }

  if (window && window.sbWeb && window.sbWeb.isUserSignedIn) {
    return new Promise((resolve) => {
      window.sbWeb.isUserSignedIn((status) => {
        resolve(finalizeConfig(getTelemetryUserData(), status));
      });
    });
  }

  const userData = getTelemetryUserData();
  return Promise.resolve(finalizeConfig(userData, userData.isLoggedIn));
}
